import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { closedSelectorLabel } from './merchantChrome.js';

export type MerchantScopeOption = {
  value: string;
  name: string;
  detail?: string;
};

const SEARCH_THRESHOLD = 8;

export function MerchantScopeSelect({
  ariaLabel,
  value,
  options,
  onChange,
  disabled,
  testId,
  branchSelector,
}: {
  ariaLabel: string;
  value: string;
  options: MerchantScopeOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  testId: string;
  branchSelector?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = options.find((option) => option.value === value) ?? options[0];
  const closed = selected ? closedSelectorLabel(selected.name) : ariaLabel;
  const showSearch = options.length >= SEARCH_THRESHOLD;
  const visibleOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.name.toLowerCase().includes(needle));
  }, [options, query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    setActiveIndex(
      Math.max(
        0,
        visibleOptions.findIndex((option) => option.value === value),
      ),
    );
    function onDoc(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, visibleOptions, value]);

  function commit(next: string) {
    if (next && next !== value) onChange(next);
    setOpen(false);
  }

  function onTriggerKey(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    }
  }

  function onListKey(event: React.KeyboardEvent<HTMLUListElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, visibleOptions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const next = visibleOptions[activeIndex];
      if (next) commit(next.value);
    }
  }

  return (
    <div ref={rootRef} className="mer-scope-select">
      <button
        type="button"
        className="mer-store-select mer-scope-select-trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        disabled={disabled}
        data-testid={testId}
        data-branch-selector={branchSelector ? 'true' : undefined}
        onClick={() => {
          if (!disabled) setOpen((v) => !v);
        }}
        onKeyDown={onTriggerKey}
      >
        {closed}
        <span className="mer-scope-select-caret" aria-hidden>
          ▾
        </span>
      </button>
      {open ? (
        <div className="mer-scope-select-menu m01-scope-menu">
          {showSearch ? (
            <label className="m01-scope-search">
              <span className="m01-sr-only">Search {ariaLabel.toLowerCase()}</span>
              <input
                type="search"
                className="mer-store-select m01-scope-search-input"
                value={query}
                placeholder={`Search ${ariaLabel.toLowerCase()}…`}
                data-testid={`${testId}-search`}
                autoFocus
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActiveIndex(0);
                }}
              />
            </label>
          ) : null}
          <ul id={listId} role="listbox" aria-label={ariaLabel} tabIndex={-1} onKeyDown={onListKey}>
            {visibleOptions.map((option, index) => {
              const selectedOption = option.value === selected?.value;
              return (
                <li
                  key={option.value}
                  id={`${listId}-${option.value}`}
                  role="option"
                  aria-selected={selectedOption}
                  className="mer-scope-select-option"
                  data-active={index === activeIndex ? 'true' : 'false'}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => commit(option.value)}
                >
                  <span className="mer-scope-select-name">{option.name}</span>
                  {option.detail ? (
                    <span className="mer-scope-select-detail">{option.detail}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
